/**
 * Renders the open file-share dialog (`fileDialogStore`) by `request.kind`; mounted once in App.tsx beside
 * `QueueDialogsHost`. Every opening mounts fresh (keyed per request), so a dialog's state and its in-flight request
 * go with it when it closes. The two delete kinds run in the app-level confirm dialog: they open it and release the slot.
 */
import { useEffect, useRef } from 'react';
import { CreateShareDialog } from './dialogs/CreateShareDialog';
import { openShareDeleteConfirm } from './dialogs/DeleteShareDialog';
import { MountCommandDialog } from './dialogs/MountCommandDialog';
import { OpenShareByNameDialog } from './dialogs/OpenShareByNameDialog';
import { ShareQuotaDialog } from './dialogs/ShareQuotaDialog';
import { ShareSnapshotsDialog } from './dialogs/ShareSnapshotsDialog';
import { openSnapshotDeleteConfirm } from './dialogs/snapshotDeleteConfirm';
import { FilePropertiesPanel } from './FilePropertiesPanel';
import { useFileDialogStore, type ShareDialogRequest } from './fileDialogStore';

type DialogProps = { request: ShareDialogRequest; onClose: () => void };

/** Opens the delete confirm once for this request, then frees the dialog slot. */
function DeleteConfirmLauncher({ request, onClose }: DialogProps) {
  const launched = useRef(false);
  useEffect(() => {
    if (launched.current) return;
    launched.current = true;
    const { connectionId, resource, source, protocol } = request;
    if (resource?.kind === 'share' && request.kind === 'delete-share') openShareDeleteConfirm({ connectionId, share: resource.share, source, protocol });
    if (resource?.kind === 'share' && resource.snapshot && request.kind === 'delete-snapshot') openSnapshotDeleteConfirm({ connectionId, share: resource.share, snapshot: resource.snapshot, source, protocol });
    onClose();
  }, [request, onClose]);
  return null;
}

function ShareDialog({ request, onClose }: DialogProps) {
  switch (request.kind) {
    case 'create-share': return <CreateShareDialog request={request} onClose={onClose} />;
    case 'share-quota': return <ShareQuotaDialog request={request} onClose={onClose} />;
    case 'share-snapshots': return <ShareSnapshotsDialog request={request} onClose={onClose} />;
    case 'mount-command': return <MountCommandDialog request={request} onClose={onClose} />;
    case 'properties': return <FilePropertiesPanel request={request} onClose={onClose} />;
    case 'delete-share':
    case 'delete-snapshot': return <DeleteConfirmLauncher request={request} onClose={onClose} />;
    case 'open-share-by-name': return null;
  }
}

const openings = new WeakMap<ShareDialogRequest, number>();
let openingCount = 0;
const openingKey = (request: ShareDialogRequest): number => {
  if (!openings.has(request)) openings.set(request, ++openingCount);
  return openings.get(request)!;
};

export function FileShareDialogsHost() {
  const request = useFileDialogStore((state) => state.request);
  const close = useFileDialogStore((state) => state.close);
  return (
    <>
      <OpenShareByNameDialog
        open={request?.kind === 'open-share-by-name'}
        connectionId={request?.connectionId ?? ''}
        source={request?.source ?? 'tree'}
        onClose={close}
      />
      {request && <ShareDialog key={openingKey(request)} request={request} onClose={close} />}
    </>
  );
}
